import { createContext, useContext, useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { getPublicLinkPageBySlug } from '../api/linkPageService';
import { transformLinkPageResponse } from '../api/linkPageTransform';

const BusinessContext = createContext();

/**
 * Extracts business slug from URL pathname
 * Example: "/mybusiness" -> "mybusiness"
 * Example: "/mybusiness/menu" -> "mybusiness"
 */
const extractSlugFromPath = (pathname) => {
  // Remove leading slash and get first segment
  const segments = pathname.split('/').filter(Boolean);
  return segments[0] || null;
};

export const BusinessProvider = ({ children }) => {
  const location = useLocation();
  const [business, setBusiness] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    const fetchBusinessData = async () => {
      const slug = extractSlugFromPath(location.pathname);

      if (!slug) {
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);
      setNotFound(false);

      try {
        // Fetch linkPage data from backend
        const backendData = await getPublicLinkPageBySlug(slug);
        
        // Transform backend response to frontend format
        const transformedData = transformLinkPageResponse(backendData);
        
        setBusiness(transformedData.business);
        setNotFound(false);
      } catch (err) {
        console.error('Error fetching business data:', err);
        
        if (err.response?.status === 404 || err.message?.includes('not found')) {
          setNotFound(true);
          setError('Business not found');
        } else {
          setError(err.message || 'Failed to load business data');
        }
        setBusiness(null);
      } finally {
        setLoading(false);
      }
    };

    fetchBusinessData();
  }, [location.pathname]);

  const value = {
    business,
    loading,
    error,
    notFound,
  };

  return (
    <BusinessContext.Provider value={value}>
      {children}
    </BusinessContext.Provider>
  );
};

/**
 * Hook to access business data from context
 * @returns {Object} { business, loading, error, notFound }
 */
export const useBusiness = () => {
  const context = useContext(BusinessContext);
  if (!context) {
    throw new Error('useBusiness must be used within a BusinessProvider');
  }
  return context;
};

